import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { Loading, EmptyState } from "@/components/States";
import { toast } from "sonner";
import { Flag, Pin, EyeOff, Trash2, MessagesSquare } from "lucide-react";

export default function CommunityMgmt() {
  const qc = useQueryClient();
  const { data: reported, isLoading: l1, refetch: r1 } = useQuery({ queryKey: ["reported"], queryFn: async () => (await api.get("/posts/reported")).data });
  const { data: posts, isLoading: l2, refetch: r2 } = useQuery({ queryKey: ["all-posts"], queryFn: async () => (await api.get("/posts")).data });

  const act = async (id, action) => {
    try {
      if (action === "delete") await api.delete(`/posts/${id}`);
      else await api.post(`/posts/${id}/${action}`);
      r1(); r2(); qc.invalidateQueries({ queryKey: ["overview"] });
      toast.success("Ação aplicada.");
    } catch (e) { toast.error(apiError(e)); }
  };

  const PostRow = ({ p, reportedMode }) => (
    <div data-testid="mgr-post" className="glass rounded-2xl p-4 card-hover">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-sm font-semibold">{p.author?.name}</span>
        <span className="text-xs text-muted-foreground">{p.author?.tower}</span>
        {p.pinned && <span className="text-[10px] text-primary">📌 fixado</span>}
        {reportedMode && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 flex items-center gap-1"><Flag className="w-3 h-3" /> denunciada</span>}
      </div>
      <p className="text-sm">{p.text}</p>
      <div className="flex gap-2 mt-3">
        <button onClick={() => act(p.id, "pin")} data-testid="mgr-pin" className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:border-primary/40 transition-colors"><Pin className="w-3.5 h-3.5" /> {p.pinned ? "Desafixar" : "Fixar"}</button>
        <button onClick={() => act(p.id, "hide")} data-testid="mgr-hide" className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:border-yellow-400/40 transition-colors"><EyeOff className="w-3.5 h-3.5" /> Ocultar</button>
        <button onClick={() => act(p.id, "delete")} data-testid="mgr-delete" className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"><Trash2 className="w-3.5 h-3.5" /> Excluir</button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display font-bold text-3xl tracking-tight">Gestão da comunidade</h1>
        <p className="text-sm text-muted-foreground">Modere publicações e mantenha um ambiente saudável.</p>
      </div>

      <section>
        <p className="font-display font-semibold mb-3 flex items-center gap-2"><Flag className="w-4 h-4 text-red-400" /> Denunciadas</p>
        {l1 ? <Loading /> : !reported?.length ? <EmptyState icon={Flag} title="Nenhuma denúncia" subtitle="Tudo tranquilo na comunidade." /> :
          <div className="grid gap-3">{reported.map((p) => <PostRow key={p.id} p={p} reportedMode />)}</div>}
      </section>

      <section>
        <p className="font-display font-semibold mb-3 flex items-center gap-2"><MessagesSquare className="w-4 h-4 text-primary" /> Publicações recentes</p>
        {l2 ? <Loading /> : !posts?.length ? <EmptyState icon={MessagesSquare} title="Sem publicações" /> :
          <div className="grid gap-3">{posts.map((p) => <PostRow key={p.id} p={p} />)}</div>}
      </section>
    </div>
  );
}
